import { VariableType } from 'json-to-graphql-query';
import { CardInputVariables, CardInputArgs, CardEditVariables, CardEditArgs } from './queryVariables/card';
import { EntityResource } from './entity.resource';
/**
 * Resource kart
 *
 * @export
 * @class CardResource
 * @extends {EntityResource<Card>}
 */
export class CardResource extends EntityResource {
    constructor() {
        super({ entityName: 'card' });
        this.getFields = {
            id: true,
            number: true,
            state: true,
            startDate: true,
            endDate: true,
            expirationType: true,
            blockEndDate: true,
            duplicate: true,
            physical: true,
            style: true
        };
        this.cardList = {
            id: true,
            number: true,
            state: true,
            startDate: true,
            endDate: true,
            expirationType: true,
            blockEndDate: true,
            created: true,
            duplicate: true,
            physical: true,
            style: true
        };
        this.listFields = {
            ...this.cardList,
            profile: {
                id: true,
                firstName: true,
                lastName: true,
                userId: true
            },
            collectionPoint: {
                name: true
            }
        };
        this.addVariables = CardInputVariables;
        this.addArgs = CardInputArgs;
        this.updateVariables = CardEditVariables;
        this.updateArgs = CardEditArgs;
        this.listVariables = { filters: 'CardFilters' };
    }
    /**
     * Lista kart użytkownika
     *
     * @param {number} id
     * @returns {Promise<Card[]>}
     * @memberof CardResource
     */
    async cardsById(id) {
        const query = this.prepareOptions({
            query: {
                __variables: { id: 'String!' },
                profile: {
                    __args: { id: new VariableType('id') },
                    id: true,
                    cards: {
                        ...this.cardList,
                        accepted: true,
                        blockReason: true,
                        inactiveReason: true,
                        collectionPoint: {
                            name: true,
                            city: true,
                            street: true,
                            postalCode: true,
                            homeNumber: true,
                            localNumber: true
                        }
                    }
                }
            }
        });
        const data = await this.query({
            query,
            variables: { id }
        }, 'profile');
        return data.cards;
    }
    /**
     * Aktualizacja kart na gotowe do odebrania
     *
     * @param {number[]} [ids]
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    setReadyState(ids) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { ids: '[Int!]' },
                cardsSetReadyState: {
                    __args: {
                        ids: new VariableType('ids')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { ids } }, 'cardsSetReadyState');
    }
    /**
     * Blokada czasowa karty
     *
     * @param {{ id: number; blockEndDate: Date }} variables
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    block(variables) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!', blockEndDate: 'DateScalar!' },
                cardBlock: { __args: { id: new VariableType('id'), blockEndDate: new VariableType('blockEndDate') } }
            }
        });
        return this.mutation({ mutation, variables }, 'cardBlock');
    }
    /**
     * Tworzenie duplikatu karty
     *
     * @param {{ cardNumber: string }} variables
     * @returns {Promise<number>}
     * @memberof CardResource
     */
    createDuplicate(cardNumber) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { cardNumber: 'String!' },
                cardAddDuplicate: {
                    __args: {
                        cardNumber: new VariableType('cardNumber')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { cardNumber } }, 'cardAddDuplicate');
    }
    /**
     * Zlecenie druku karty
     *
     * @param {number} id
     * @param {number} collectioPointId
     * @returns {Promise<number>}
     * @memberof CardResource
     */
    printCard(id, collectionPointId) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!', collectionPointId: 'Int!' },
                cardOrderPrint: {
                    __args: {
                        id: new VariableType('id'),
                        collectionPointId: new VariableType('collectionPointId')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { id, collectionPointId } }, 'cardOrderPrint');
    }
    /**
     * Deaktywacja karty
     *
     * @param {{ id: number }} variables
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    deactivate(variables) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!' },
                cardDeactivate: { __args: { id: new VariableType('id') } }
            }
        });
        return this.mutation({ mutation, variables }, 'cardDeactivate');
    }
    /**
     * Obiór karty
     *
     * @param {{ id: number; collectDate: Date }} variables
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    collected(variables) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!', collectDate: 'DateScalar!' },
                cardCollected: {
                    __args: {
                        id: new VariableType('id'),
                        collectDate: new VariableType('collectDate')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables }, 'cardCollected');
    }
    /**
     * Karty zostały przygotowane do rozwiezienia
     *
     * @param {number[]} [ids]
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    setPreparedState(ids) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { ids: '[Int!]' },
                cardsSetPreparedState: {
                    __args: {
                        ids: new VariableType('ids')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { ids } }, 'cardsSetPreparedState');
    }
    /**
     * Przekazanie przygotowanych kart operatorowi
     *
     * @param {number[]} [ids]
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    setTransportState(ids) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { ids: '[Int!]' },
                cardsSetTransportState: {
                    __args: {
                        ids: new VariableType('ids')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { ids } }, 'cardsSetTransportState');
    }
    /**
     * Przekazanie kart zamówionych do druku
     *
     * @param {number[]} [ids]
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    setPreparedToPrintState(ids) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { ids: '[Int!]' },
                cardsSetPrepareToPrintState: {
                    __args: {
                        ids: new VariableType('ids')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { ids } }, 'cardsSetPrepareToPrintState');
    }
    /**
     * Przedłużanie ważności karty o rok
     *
     * @param {number} id
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    extendExpirationDateByYear(id) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!' },
                cardExtendExpirationByYear: {
                    __args: {
                        id: new VariableType('id')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { id } }, 'cardExtendExpirationByYear');
    }
    /**
     * Skrócenie ważności karty o rok
     *
     * @param {number} id
     * @returns {Promise<void>}
     * @memberof CardResource
     */
    reduceExpirationDateByYear(id) {
        const mutation = this.prepareOptions({
            mutation: {
                __variables: { id: 'Int!' },
                cardReduceExpirationDateByYear: {
                    __args: {
                        id: new VariableType('id')
                    }
                }
            }
        });
        return this.mutation({ mutation, variables: { id } }, 'cardReduceExpirationDateByYear');
    }
}
export const cardResource = new CardResource();
//# sourceMappingURL=card.resource.js.map